// RootLayout.tsx (FINAL VERSION FOR GLOBAL LOADING)

import { Outlet, useNavigation, useLocation } from 'react-router-dom';
import { Suspense } from 'react';
import { AppLoader } from './components/_misc/layouts/app-loader/AppLoader';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { ErrorFallbackContainer } from './components/_misc/layouts/error/ErrorFallbackContainer';
import { LazyMotion, domAnimation } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { useSiteColors } from './hooks/useSiteColors';

// Define the fallback function here (assuming this part works)
const renderFallback = (fallbackProps: FallbackProps) => (
  <ErrorFallbackContainer
    title="Error!!"
    subTitle="We're sorry it's come to this......"
    {...fallbackProps}
  />
);

// --- 1. Internal Component for Global Navigation Loading ---
const GlobalLoadingBar = () => {
  const navigation = useNavigation();

  // The bar is visible when the router is processing a new page or submission
  const isNavigating = navigation.state !== 'idle';

  if (!isNavigating) {
    return null;
  }

  // Your existing animated loading bar component
  return (
    <div className="fixed left-0 right-0 top-0 z-[9999] h-1 animate-pulse bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
  );
};

export function RootLayout() {
  const location = useLocation();

  return (
    <>
      {/* 2. Place the global loading bar above everything, so it's always visible */}
      <GlobalLoadingBar />

      {/* 3. ErrorBoundary wraps the main content */}
      {/* key={location.pathname} forces error boundary to reset on navigation */}
      <ErrorBoundary
        key={location.pathname}
        FallbackComponent={({ error, resetErrorBoundary }) => renderFallback({ error, resetErrorBoundary })}
        onError={(error, info) => {
          try {
            Sentry.captureException(error, {
              contexts: { react: { componentStack: info.componentStack } },
            });
          } catch {}
        }}
        onReset={() => console.log('todo')}
      >
        {/* 4. Suspense catches the loading of any lazy route component */}
        <LazyMotion features={domAnimation} strict>
          <Suspense fallback={<AppLoader />}>
            <Outlet />
          </Suspense>
        </LazyMotion>
      </ErrorBoundary>
    </>
  );
}
